import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl, asList, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BatchCardScanner } from "@/components/BatchCardScanner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Camera, MapPin, Loader2, Check, X, Users, ReceiptText,
  Plus, ArrowLeft, Wallet, IndianRupee, Route, Navigation,
  UploadCloud, User as UserIcon, Building2, Phone, Mail, Globe, Calendar,
  ThumbsUp, ThumbsDown, CheckCircle2, Image as ImageIcon,
  Download, MessageCircle,
} from "lucide-react";
import { downloadVCard } from "@/lib/contactExport";
import { WhatsAppShareDialog } from "@/components/WhatsAppShareDialog";

// ---------- location helpers ----------

/**
 * Ask the browser for the user's current position. Returns null if denied or unsupported.
 * Deliberately non-blocking: caller shows a permission-denied hint but continues.
 */
function useGeolocation() {
  const [state, setState] = useState({ loading: false, lat: null, lng: null, error: null });
  const capture = () =>
    new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        setState({ loading: false, lat: null, lng: null, error: "unsupported" });
        resolve(null);
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const out = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setState({ loading: false, ...out, error: null });
          resolve(out);
        },
        (err) => {
          setState({ loading: false, lat: null, lng: null, error: err.message || "denied" });
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
      );
    });
  return { ...state, capture };
}

function mapsLink(lat, lng) {
  if (lat == null || lng == null) return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

// Upload one File to the existing /upload endpoint. Returns {url, thumb_url}.
async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

// ---------- Shared page header ----------
function PageHeader({ eyebrow, title, actions }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">{eyebrow}</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">{title}</h1>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

// =====================================================================
// SCAN CARD
// =====================================================================
export function ScanCardPage() {
  const nav = useNavigate();
  const geo = useGeolocation();
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [batchMode, setBatchMode] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | scanning | review | saving | done
  const [scanResult, setScanResult] = useState(null); // {parsed, image_url, thumb_url, duplicate_contact_id, duplicate_contact_name}
  const [address, setAddress] = useState("");
  const [error, setError] = useState("");
  const [waOpen, setWaOpen] = useState(false);

  const onCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setPhase("scanning");
    // Capture location in parallel — do not block on it.
    geo.capture();
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/field-sales/scan-card", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScanResult(data);
      setPhase("review");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Card scan failed");
      setPhase("idle");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const updateParsed = (patch) => setScanResult((s) => ({ ...s, parsed: { ...s.parsed, ...patch } }));

  const save = async ({ merge }) => {
    if (!scanResult) return;
    setPhase("saving");
    try {
      const payload = {
        parsed: scanResult.parsed,
        image_url: scanResult.image_url,
        thumb_url: scanResult.thumb_url,
        latitude: geo.lat,
        longitude: geo.lng,
        address: address || null,
        merge_into_contact_id: merge ? scanResult.duplicate_contact_id : null,
      };
      const { data } = await api.post("/field-sales/scan-card/save", payload);
      toast.success(merge ? "Merged into existing contact" : "Contact saved");
      setPhase("done");
      nav(`/contacts/${data.contact_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
      setPhase("review");
    }
  };

  return (
    <div className="space-y-6" data-testid="scan-card-page">
      <PageHeader
        eyebrow="Field Sales"
        title={batchMode ? "Batch Card Scan" : "Scan Visiting Card"}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={batchMode ? "default" : "outline"}
              className={`rounded-full ${batchMode ? "bg-[#0F5E39] hover:bg-[#083F22] text-white" : "border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10"}`}
              onClick={() => setBatchMode((v) => !v)}
              data-testid="toggle-batch-mode"
            >
              {batchMode ? "Single card" : "Batch mode"}
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => nav(-1)} data-testid="scan-back-btn">
              <ArrowLeft size={14} className="mr-1"/> Back
            </Button>
          </div>
        }
      />

      {batchMode ? (
        <BatchCardScanner
          mode="crm"
          initialGeo={geo.lat != null ? { lat: geo.lat, lng: geo.lng } : null}
          onDone={() => { /* stay in review-done state; user can scan more */ }}
        />
      ) : (
      <>
      {phase === "idle" && (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-6 text-center space-y-4">
            <div className="mx-auto w-24 h-24 rounded-full bg-[#F1EDDF] flex items-center justify-center">
              <Camera size={40} className="text-[#0F5E39]" strokeWidth={1.5}/>
            </div>
            <div>
              <div className="font-serif text-2xl">Capture a card</div>
              <p className="text-[#4A5A50] text-sm mt-1">Point your camera at the card. Any language — we&apos;ll read it.</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onCapture}
              className="hidden"
              data-testid="scan-file-input"
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={onCapture}
              className="hidden"
              data-testid="scan-gallery-input"
            />
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                size="lg"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-14 px-8 text-base"
                data-testid="scan-open-camera-btn"
              >
                <Camera size={20} className="mr-2"/> Open Camera
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => galleryInputRef.current?.click()}
                className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10 h-14 px-8 text-base"
                data-testid="scan-upload-gallery-btn"
              >
                <ImageIcon size={20} className="mr-2"/> Upload from Gallery
              </Button>
            </div>
            <div className="text-xs text-[#4A5A50]">
              Camera on mobile · file upload on desktop. Location captured if you allow permission.
            </div>
            {error && <div className="text-sm text-red-600" data-testid="scan-error">{error}</div>}
          </CardContent>
        </Card>
      )}

      {phase === "scanning" && (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="p-10 text-center space-y-3" data-testid="scan-loading">
            <Loader2 className="animate-spin text-[#0F5E39] mx-auto" size={36}/>
            <div className="font-serif text-xl">Reading the card…</div>
            <p className="text-[#4A5A50] text-sm">Detecting language, extracting name, phones, email, address.</p>
          </CardContent>
        </Card>
      )}

      {(phase === "review" || phase === "saving") && scanResult && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border-[#E4DFCF] rounded-xl lg:col-span-1">
            <CardContent className="p-4">
              <img
                src={fileUrl(scanResult.thumb_url || scanResult.image_url)}
                alt="Captured card"
                className="w-full rounded-lg border border-[#E4DFCF]"
                data-testid="scan-preview-img"
              />
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[#4A5A50]">Detected language</div>
              <div className="text-sm">{scanResult.parsed.detected_language || "—"}</div>
              <div className="mt-3 flex items-center gap-1 text-xs text-[#4A5A50]">
                <MapPin size={12}/>
                {geo.loading ? "Locating…" : (geo.lat != null ? (
                  <a className="underline" href={mapsLink(geo.lat, geo.lng)} target="_blank" rel="noreferrer">
                    {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}
                  </a>
                ) : (geo.error ? "Location unavailable" : "Location not captured"))}
              </div>
              <div className="mt-4">
                <Label className="text-[10px] uppercase tracking-widest">Address (optional)</Label>
                <Textarea
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g. Zaveri Bazaar, Mumbai"
                  className="mt-1"
                  data-testid="scan-address-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E4DFCF] rounded-xl lg:col-span-2">
            <CardContent className="p-6 space-y-4" data-testid="scan-review-form">
              {scanResult.duplicate_contact_id && (
                <div className="bg-[#F6EED8] border border-[#C9A24B]/40 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="scan-duplicate-hint">
                  <Users size={16} className="text-[#6E5416] shrink-0 mt-0.5"/>
                  <div>
                    <div className="font-medium text-[#6E5416]">Possible match: {scanResult.duplicate_contact_name}</div>
                    <div className="text-[#4A5A50] text-xs">A contact with the same phone/email already exists. Save as new, or merge into that contact.</div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldRow label="Name" icon={UserIcon}>
                  <Input value={scanResult.parsed.name} onChange={(e) => updateParsed({ name: e.target.value })} data-testid="scan-field-name"/>
                </FieldRow>
                <FieldRow label="Company" icon={Building2}>
                  <Input value={scanResult.parsed.company} onChange={(e) => updateParsed({ company: e.target.value })} data-testid="scan-field-company"/>
                </FieldRow>
                <FieldRow label="Designation" icon={UserIcon}>
                  <Input value={scanResult.parsed.designation} onChange={(e) => updateParsed({ designation: e.target.value })} data-testid="scan-field-designation"/>
                </FieldRow>
                <FieldRow label="Primary phone" icon={Phone}>
                  <Input
                    value={scanResult.parsed.phones[0] || ""}
                    onChange={(e) => {
                      const rest = scanResult.parsed.phones.slice(1);
                      updateParsed({ phones: [e.target.value, ...rest].filter(Boolean) });
                    }}
                    data-testid="scan-field-phone"
                  />
                </FieldRow>
                <FieldRow label="Email" icon={Mail}>
                  <Input
                    value={scanResult.parsed.emails[0] || ""}
                    onChange={(e) => {
                      const rest = scanResult.parsed.emails.slice(1);
                      updateParsed({ emails: [e.target.value, ...rest].filter(Boolean) });
                    }}
                    data-testid="scan-field-email"
                  />
                </FieldRow>
                <FieldRow label="Website" icon={Globe}>
                  <Input
                    value={scanResult.parsed.websites[0] || ""}
                    onChange={(e) => updateParsed({ websites: [e.target.value].filter(Boolean) })}
                    data-testid="scan-field-website"
                  />
                </FieldRow>
              </div>

              <FieldRow label="Card address" icon={MapPin}>
                <Textarea
                  rows={2}
                  value={scanResult.parsed.address}
                  onChange={(e) => updateParsed({ address: e.target.value })}
                  data-testid="scan-field-address"
                />
              </FieldRow>

              {scanResult.parsed.raw_text && (
                <details className="text-xs text-[#4A5A50]" data-testid="scan-raw-details">
                  <summary className="cursor-pointer">Show raw text extracted</summary>
                  <pre className="mt-2 whitespace-pre-wrap bg-[#F1EDDF] p-3 rounded-lg font-mono text-[11px]">{scanResult.parsed.raw_text}</pre>
                </details>
              )}

              {error && <div className="text-sm text-red-600" data-testid="scan-save-error">{error}</div>}

              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" className="rounded-full" onClick={() => { setScanResult(null); setPhase("idle"); }} data-testid="scan-discard-btn">
                  <X size={14} className="mr-1"/> Discard & re-scan
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10"
                  onClick={() => downloadVCard(scanResult.parsed, { address: address || scanResult.parsed.address })}
                  disabled={!scanResult.parsed.name?.trim() && !(scanResult.parsed.phones || [])[0]}
                  data-testid="scan-save-phone-btn"
                >
                  <Download size={14} className="mr-1"/> Save to phone
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                  onClick={() => setWaOpen(true)}
                  data-testid="scan-whatsapp-btn"
                >
                  <MessageCircle size={14} className="mr-1"/> WhatsApp
                </Button>
                {scanResult.duplicate_contact_id && (
                  <Button variant="outline" className="rounded-full border-[#C9A24B] text-[#6E5416]" disabled={phase === "saving"} onClick={() => save({ merge: true })} data-testid="scan-merge-btn">
                    <Users size={14} className="mr-1"/> Merge into existing
                  </Button>
                )}
                <Button
                  className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white ml-auto"
                  disabled={phase === "saving" || !scanResult.parsed.name.trim()}
                  onClick={() => save({ merge: false })}
                  data-testid="scan-save-btn"
                >
                  {phase === "saving" ? <Loader2 className="animate-spin mr-2" size={14}/> : <Check size={14} className="mr-1"/>}
                  Save as new contact
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {scanResult && (
        <WhatsAppShareDialog
          open={waOpen}
          onOpenChange={setWaOpen}
          parsed={scanResult.parsed}
        />
      )}
      </>
      )}
    </div>
  );
}

function FieldRow({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50] flex items-center gap-1">
        {Icon ? <Icon size={12}/> : null} {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// =====================================================================
// MY VISITS (list + check-in flow)
// =====================================================================
export function MyVisitsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCheckIn, setOpenCheckIn] = useState(false);

  const canViewTeam = can(user, "field.team.view");
  const [scope, setScope] = useState("me"); // 'me' or a salesperson id
  const [team, setTeam] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (canViewTeam && scope !== "me") params.salesperson_id = scope;
      const { data } = await api.get("/field-sales/visits", { params });
      setRows(asList(data));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scope]);
  useEffect(() => {
    if (!canViewTeam) return;
    (async () => {
      try { const { data } = await api.get("/field-sales/team/salespeople"); setTeam(asList(data)); } catch (err) { void err; }
    })();
  }, [canViewTeam]);

  return (
    <div className="space-y-6" data-testid="my-visits-page">
      <PageHeader
        eyebrow="Field Sales"
        title="Visits"
        actions={
          <>
            <Button variant="outline" className="rounded-full" onClick={() => nav("/field/scan")} data-testid="visits-scan-btn">
              <Camera size={14} className="mr-1"/> Scan Card
            </Button>
            <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setOpenCheckIn(true)} data-testid="visits-checkin-btn">
              <Plus size={14} className="mr-1"/> Check-in
            </Button>
          </>
        }
      />

      {canViewTeam && (
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">View</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-64" data-testid="visits-scope-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="me">My visits</SelectItem>
              {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? <div className="text-center py-12 text-[#4A5A50]" data-testid="visits-loading">Loading…</div> :
        rows.length === 0 ? (
          <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-16 text-center">
            <Route size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
            <div className="font-serif text-xl">No visits yet</div>
            <div className="text-[#4A5A50] text-sm mt-1">Check-in when you meet a customer, or scan a business card to log a visit.</div>
          </CardContent></Card>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(v => <VisitCard key={v.id} v={v} onOpen={() => nav(`/field/visits/${v.id}`)}/>)}
        </div>
      )}

      <CheckInDialog open={openCheckIn} onClose={() => setOpenCheckIn(false)} onCreated={() => { setOpenCheckIn(false); load(); }}/>
    </div>
  );
}

function VisitCard({ v, onOpen }) {
  const dt = v.check_in_at ? new Date(v.check_in_at) : null;
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white rounded-xl border border-[#E4DFCF] p-5 card-hover"
      data-testid={`visit-card-${v.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-serif text-xl truncate">{v.contact_name || v.purpose || "Field visit"}</div>
          {v.contact_name && v.purpose && <div className="text-sm text-[#4A5A50] mt-0.5 truncate">{v.purpose}</div>}
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusPill status={v.status}/>
          {v.source === "card_scan" && <span className="text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2 py-0.5 rounded-full">Card</span>}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1 text-xs text-[#4A5A50]">
        <Calendar size={12}/>{dt ? format(dt, "dd MMM yyyy · HH:mm") : "—"}
      </div>
      {(v.check_in_address || (v.check_in_lat && v.check_in_lng)) && (
        <div className="mt-1 flex items-center gap-1 text-xs text-[#4A5A50]">
          <MapPin size={12}/>
          <span className="truncate">{v.check_in_address || `${v.check_in_lat?.toFixed(4)}, ${v.check_in_lng?.toFixed(4)}`}</span>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-[#E4DFCF] text-xs text-[#4A5A50] truncate">
        By {v.salesperson_name || "—"}
      </div>
    </button>
  );
}

function CheckInDialog({ open, onClose, onCreated }) {
  const geo = useGeolocation();
  const [form, setForm] = useState({ purpose: "", notes: "", address: "" });
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ purpose: "", notes: "", address: "" });
    setContactId("");
    setError("");
    geo.capture();
    (async () => {
      try { const { data } = await api.get("/contacts", { params: { page_size: 200 } }); setContacts(asList(data)); } catch (err) { void err; }
    })();
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post("/field-sales/visits", {
        contact_id: contactId || null,
        purpose: form.purpose || null,
        notes: form.notes || null,
        latitude: geo.lat,
        longitude: geo.lng,
        address: form.address || null,
      });
      toast.success("Checked in");
      onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkin-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Check-in</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Contact (optional)</Label>
            <Select value={contactId || undefined} onValueChange={setContactId}>
              <SelectTrigger className="mt-1" data-testid="checkin-contact-select"><SelectValue placeholder="Pick contact"/></SelectTrigger>
              <SelectContent className="max-h-72">
                {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.mobile ? "· " + c.mobile : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Purpose</Label>
            <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g. Product demo, follow-up" data-testid="checkin-purpose"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkin-notes"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Address (optional)</Label>
            <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkin-address"/>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#4A5A50]" data-testid="checkin-geo-status">
            <MapPin size={12}/>
            {geo.loading ? "Getting location…" : geo.lat != null ? (
              <>Location captured · <a className="underline" target="_blank" rel="noreferrer" href={mapsLink(geo.lat, geo.lng)}>view</a></>
            ) : geo.error ? "Location denied — address is optional" : "Location not captured"}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="checkin-submit-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={14}/> : <Check size={14} className="mr-1"/>} Check in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VisitDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/field-sales/visits/${id}`); setV(data); }
    catch { toast.error("Visit not found"); nav("/field/visits"); }
  };
  useEffect(() => { load(); }, [id]);

  if (!v) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  const isOwner = v.salesperson_id === user?.id;
  const canCheckOut = isOwner && v.status === "open";

  return (
    <div className="space-y-6" data-testid="visit-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Visit</div>
          <h1 className="font-serif text-3xl font-medium">{v.contact_name || v.purpose || "Field visit"}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            <StatusPill status={v.status}/>
            {v.source === "card_scan" && <span className="text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full">Card scan</span>}
          </div>
        </div>
        {canCheckOut && (
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setCheckOutOpen(true)} data-testid="visit-checkout-btn">
            <CheckCircle2 size={14} className="mr-1"/> Check out
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-[#E4DFCF] rounded-xl lg:col-span-2">
          <CardContent className="p-6 space-y-4">
            <Stat label="Salesperson" value={v.salesperson_name || "—"}/>
            {v.contact_name && (
              <Stat label="Contact">
                <Link to={`/contacts/${v.contact_id}`} className="underline">{v.contact_name}</Link>
              </Stat>
            )}
            {v.purpose && <Stat label="Purpose" value={v.purpose}/>}
            {v.notes && <Stat label="Notes" value={v.notes}/>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LocationBlock label="Check-in" at={v.check_in_at} lat={v.check_in_lat} lng={v.check_in_lng} address={v.check_in_address}/>
              <LocationBlock label="Check-out" at={v.check_out_at} lat={v.check_out_lat} lng={v.check_out_lng} address={v.check_out_address}/>
            </div>
          </CardContent>
        </Card>
        {v.photos?.length > 0 && (
          <Card className="border-[#E4DFCF] rounded-xl">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">Photos</div>
              <div className="grid grid-cols-2 gap-2">
                {v.photos.map((p, i) => (
                  <a key={p.id || i} href={fileUrl(p.url)} target="_blank" rel="noreferrer" className="block">
                    <img src={fileUrl(p.thumb_url || p.url)} alt="" className="w-full h-32 object-cover rounded-lg border border-[#E4DFCF]"/>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <CheckOutDialog open={checkOutOpen} onClose={() => setCheckOutOpen(false)} visitId={id} onDone={() => { setCheckOutOpen(false); load(); }}/>
    </div>
  );
}

function Stat({ label, value, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      <div className="text-sm mt-0.5">{children ?? (value || "—")}</div>
    </div>
  );
}

function LocationBlock({ label, at, lat, lng, address }) {
  const has = lat != null || address || at;
  return (
    <div className={`rounded-lg border p-3 ${has ? "border-[#E4DFCF] bg-white" : "border-dashed border-[#E4DFCF] bg-[#FAF7EF]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      {at ? (
        <>
          <div className="text-sm mt-0.5">{format(new Date(at), "dd MMM yyyy · HH:mm")}</div>
          <div className="text-xs text-[#4A5A50] mt-1 flex items-center gap-1">
            <MapPin size={12}/>
            {address || (lat != null ? <a href={mapsLink(lat, lng)} target="_blank" rel="noreferrer" className="underline">{lat.toFixed(4)}, {lng.toFixed(4)}</a> : "—")}
          </div>
        </>
      ) : <div className="text-xs text-[#4A5A50] mt-0.5">Pending</div>}
    </div>
  );
}

function CheckOutDialog({ open, onClose, visitId, onDone }) {
  const geo = useGeolocation();
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setNotes(""); setAddress(""); setError(""); geo.capture(); } }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/field-sales/visits/${visitId}/check-out`, {
        latitude: geo.lat, longitude: geo.lng, address: address || null, notes: notes || null,
      });
      toast.success("Checked out");
      onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="checkout-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Check out</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Address (optional)</Label>
            <Textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} data-testid="checkout-address"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Closing notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes"/>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#4A5A50]">
            <MapPin size={12}/>
            {geo.loading ? "Locating…" : geo.lat != null ? "Location captured" : geo.error ? "Location denied" : "No location"}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="checkout-submit-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={14}/> : <CheckCircle2 size={14} className="mr-1"/>} Check out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// EXPENSES
// =====================================================================
const MODES = ["bike", "car", "train", "bus", "flight", "auto", "other"];
const EXPENSE_STATUSES = ["submitted", "approved", "rejected", "reimbursed"];

export function MyExpensesPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const canApprove = can(user, "field.expenses.approve");
  const canReimburse = can(user, "field.expenses.reimburse");
  const canViewTeam = canApprove || canReimburse;

  const [scope, setScope] = useState("me");
  const [team, setTeam] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (canViewTeam && scope !== "me") params.salesperson_id = scope;
      const { data } = await api.get("/field-sales/expenses", { params });
      setRows(asList(data));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, scope]);
  useEffect(() => {
    if (!canViewTeam) return;
    (async () => { try { const { data } = await api.get("/field-sales/team/salespeople"); setTeam(asList(data)); } catch (err) { void err; } })();
  }, [canViewTeam]);

  const total = useMemo(() => rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [rows]);

  return (
    <div className="space-y-6" data-testid="my-expenses-page">
      <PageHeader
        eyebrow="Field Sales"
        title="Travel Expenses"
        actions={
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setOpenNew(true)} data-testid="expense-new-btn">
            <Plus size={14} className="mr-1"/> New Expense
          </Button>
        }
      />

      <div className="flex items-center gap-3 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="expense-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {EXPENSE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {canViewTeam && (
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-56" data-testid="expense-scope-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="me">My expenses</SelectItem>
              {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto text-sm text-[#4A5A50]">
          <span className="text-[10px] uppercase tracking-widest">Total</span> <span className="font-medium text-[#0F1E15]">₹ {total.toLocaleString("en-IN")}</span>
        </div>
      </div>

      {loading ? <div className="text-center py-12 text-[#4A5A50]" data-testid="expenses-loading">Loading…</div> :
        rows.length === 0 ? (
          <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-16 text-center">
            <ReceiptText size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
            <div className="font-serif text-xl">No expenses</div>
            <div className="text-[#4A5A50] text-sm mt-1">Log your first travel expense.</div>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map(r => <ExpenseCard key={r.id} exp={r} onEdit={() => setEditing(r)} canApprove={canApprove} canReimburse={canReimburse} onChange={load}/>)}
          </div>
        )}

      <ExpenseFormDialog open={openNew} onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }}/>
      <ExpenseFormDialog open={!!editing} exp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>
    </div>
  );
}

function ExpenseCard({ exp, onEdit, canApprove, canReimburse, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const isOwner = exp.salesperson_id === user?.id;
  const editable = isOwner && (exp.status === "submitted" || exp.status === "rejected");

  const doApprove = async () => {
    setBusy(true);
    try { await api.post(`/field-sales/expenses/${exp.id}/approve`); toast.success("Approved"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const doReimburse = async () => {
    const ref = window.prompt("Reimbursement reference (UPI/txn):", "");
    setBusy(true);
    try { await api.post(`/field-sales/expenses/${exp.id}/reimburse`, { reference: ref || null }); toast.success("Reimbursed"); onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E4DFCF] p-5 space-y-3" data-testid={`expense-card-${exp.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{exp.mode}</div>
          <div className="font-serif text-2xl mt-0.5">₹ {Number(exp.amount).toLocaleString("en-IN")}</div>
          <div className="text-sm text-[#4A5A50] mt-1 flex items-center gap-1"><Route size={12}/> {exp.from_location} → {exp.to_location}</div>
        </div>
        <StatusPill status={exp.status}/>
      </div>
      <div className="text-xs text-[#4A5A50] flex items-center gap-1"><Calendar size={12}/> {exp.date ? format(new Date(exp.date), "dd MMM yyyy") : "—"}{exp.distance_km ? ` · ${exp.distance_km} km` : ""}</div>
      {exp.purpose && <div className="text-sm">{exp.purpose}</div>}
      {exp.rejected_reason && <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2"><strong>Rejected:</strong> {exp.rejected_reason}</div>}
      {exp.reimbursement_reference && <div className="text-xs text-[#4A5A50]">Ref: <span className="font-mono">{exp.reimbursement_reference}</span></div>}
      {exp.receipt_url && (
        <a href={fileUrl(exp.receipt_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs underline text-[#0F5E39]">
          <ReceiptText size={12}/> Receipt
        </a>
      )}
      <div className="pt-2 border-t border-[#E4DFCF] flex items-center justify-between gap-2 text-xs">
        <div className="text-[#4A5A50] truncate">By {exp.salesperson_name || "—"}</div>
        <div className="flex items-center gap-1">
          {editable && <Button size="sm" variant="outline" onClick={onEdit} data-testid={`expense-edit-${exp.id}`}>Edit</Button>}
          {canApprove && exp.status === "submitted" && (
            <>
              <Button size="sm" variant="outline" disabled={busy} onClick={doApprove} data-testid={`expense-approve-${exp.id}`} className="border-[#0F5E39] text-[#0F5E39]"><ThumbsUp size={12} className="mr-1"/> Approve</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejectOpen(true)} data-testid={`expense-reject-${exp.id}`} className="border-red-500 text-red-600"><ThumbsDown size={12} className="mr-1"/> Reject</Button>
            </>
          )}
          {canReimburse && exp.status === "approved" && (
            <Button size="sm" disabled={busy} onClick={doReimburse} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid={`expense-reimburse-${exp.id}`}><Wallet size={12} className="mr-1"/> Reimburse</Button>
          )}
        </div>
      </div>
      <RejectDialog open={rejectOpen} onClose={() => setRejectOpen(false)} expId={exp.id} onDone={() => { setRejectOpen(false); onChange(); }}/>
    </div>
  );
}

function RejectDialog({ open, onClose, expId, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try { await api.post(`/field-sales/expenses/${expId}/reject`, { reason }); toast.success("Rejected"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader><DialogTitle>Reject expense</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Reason (required)</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason"/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-submit-btn">Reject</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseFormDialog({ open, onClose, onSaved, exp }) {
  const isEdit = !!exp;
  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    from_location: "", to_location: "", distance_km: "", mode: "bike", amount: "", purpose: "",
    receipt_url: "", receipt_thumb_url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (exp) {
      setForm({
        date: exp.date ? format(new Date(exp.date), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        from_location: exp.from_location || "", to_location: exp.to_location || "",
        distance_km: exp.distance_km ?? "",
        mode: exp.mode || "bike",
        amount: exp.amount ?? "",
        purpose: exp.purpose || "",
        receipt_url: exp.receipt_url || "",
        receipt_thumb_url: exp.receipt_thumb_url || "",
      });
    } else {
      setForm({
        date: format(new Date(), "yyyy-MM-dd"),
        from_location: "", to_location: "", distance_km: "", mode: "bike", amount: "", purpose: "",
        receipt_url: "", receipt_thumb_url: "",
      });
    }
  }, [open, exp]);

  const onReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url, thumb_url } = await uploadImage(file);
      setForm(f => ({ ...f, receipt_url: url, receipt_thumb_url: thumb_url }));
    } catch { toast.error("Receipt upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.from_location.trim() || !form.to_location.trim()) { setError("From & To are required"); return; }
    const amt = Number(form.amount);
    if (Number.isNaN(amt) || amt < 0) { setError("Amount must be a non-negative number"); return; }
    setSaving(true); setError("");
    try {
      const payload = {
        date: new Date(form.date).toISOString(),
        from_location: form.from_location.trim(),
        to_location: form.to_location.trim(),
        distance_km: form.distance_km === "" ? null : Number(form.distance_km),
        mode: form.mode,
        amount: amt,
        purpose: form.purpose.trim() || null,
        receipt_url: form.receipt_url || null,
        receipt_thumb_url: form.receipt_thumb_url || null,
      };
      if (isEdit) {
        await api.patch(`/field-sales/expenses/${exp.id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/field-sales/expenses", payload);
        toast.success("Expense submitted");
      }
      onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isEdit ? "Edit expense" : "Log travel expense"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">From</Label>
            <Input value={form.from_location} onChange={(e) => setForm({ ...form, from_location: e.target.value })} data-testid="expense-from"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">To</Label>
            <Input value={form.to_location} onChange={(e) => setForm({ ...form, to_location: e.target.value })} data-testid="expense-to"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Distance (km)</Label>
            <Input type="number" step="0.1" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="expense-distance"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger data-testid="expense-mode"><SelectValue/></SelectTrigger>
              <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Amount (₹)</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount"/>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Purpose</Label>
            <Textarea rows={2} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="expense-purpose"/>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px] uppercase tracking-widest">Bill / receipt</Label>
            <div className="mt-1 flex items-center gap-3">
              {form.receipt_thumb_url ? (
                <img src={fileUrl(form.receipt_thumb_url)} alt="receipt" className="w-16 h-16 rounded-lg border border-[#E4DFCF] object-cover"/>
              ) : (
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-[#E4DFCF] flex items-center justify-center text-[#4A5A50]"><ReceiptText size={20}/></div>
              )}
              <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-[#0F5E39] underline">
                {uploading ? <><Loader2 size={14} className="animate-spin"/> Uploading…</> : <><UploadCloud size={14}/> {form.receipt_url ? "Replace" : "Upload"}</>}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onReceipt} data-testid="expense-receipt-input"/>
              </label>
            </div>
          </div>
        </div>
        {error && <div className="text-sm text-red-600" data-testid="expense-error">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="expense-submit-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={14}/> : <Check size={14} className="mr-1"/>} {isEdit ? "Save" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// TEAM JOURNEY (manager / admin / accounts)
// =====================================================================
export function TeamJourneyPage() {
  const [team, setTeam] = useState([]);
  const [salesperson, setSalesperson] = useState("all");
  const [dateFrom, setDateFrom] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseSummary, setExpenseSummary] = useState({ total: {}, by_salesperson: [] });

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/field-sales/team/salespeople"); setTeam(asList(data)); } catch (err) { void err; } })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (salesperson !== "all") params.salesperson_id = salesperson;
      const { data: v } = await api.get("/field-sales/visits", { params });
      setRows(asList(v));
      const { data: s } = await api.get("/field-sales/expenses/summary", {
        params: { period_start: dateFrom, period_end: dateTo },
      });
      setExpenseSummary(s || { total: {}, by_salesperson: [] });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [salesperson, dateFrom, dateTo]);

  const pins = rows.filter(r => r.check_in_lat != null && r.check_in_lng != null);
  const grand = expenseSummary.total || {};

  return (
    <div className="space-y-6" data-testid="team-journey-page">
      <PageHeader eyebrow="Field Sales" title="Team Journey"/>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Salesperson</Label>
            <Select value={salesperson} onValueChange={setSalesperson}>
              <SelectTrigger data-testid="journey-salesperson-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All salespeople</SelectItem>
                {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="journey-date-from"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="journey-date-to"/>
          </div>
          <div className="flex items-end">
            <div className="w-full grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-[#F1EDDF] p-2"><div className="text-[10px] uppercase text-[#4A5A50]">Visits</div><div className="text-lg font-medium">{rows.length}</div></div>
              <div className="rounded-lg bg-[#F1EDDF] p-2"><div className="text-[10px] uppercase text-[#4A5A50]">With GPS</div><div className="text-lg font-medium">{pins.length}</div></div>
              <div className="rounded-lg bg-[#F1EDDF] p-2"><div className="text-[10px] uppercase text-[#4A5A50]">Approved</div><div className="text-lg font-medium">₹ {Math.round(grand.approved || 0).toLocaleString("en-IN")}</div></div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">Map</div>
          <JourneyMap pins={pins}/>
          <div className="text-xs text-[#4A5A50] mt-2">Location is captured at check-in and check-out — PWAs cannot track continuous background GPS. For that, a native app would be needed.</div>
        </CardContent>
      </Card>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">Timeline</div>
          {loading ? <div className="text-center text-[#4A5A50] py-8">Loading…</div> : rows.length === 0 ? (
            <div className="text-center text-[#4A5A50] py-6 text-sm">No visits in this range.</div>
          ) : (
            <div className="space-y-2">
              {rows.map(v => (
                <Link key={v.id} to={`/field/visits/${v.id}`} className="block border border-[#E4DFCF] rounded-lg p-3 hover:bg-[#F1EDDF]/50">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{v.salesperson_name}</div>
                      <div className="text-xs text-[#4A5A50] truncate">{v.contact_name || v.purpose || "Field visit"}</div>
                    </div>
                    <div className="text-right text-xs text-[#4A5A50] shrink-0">
                      <div>{v.check_in_at ? format(new Date(v.check_in_at), "HH:mm · dd MMM") : "—"}</div>
                      <div>{v.check_in_address || (v.check_in_lat != null ? `${v.check_in_lat.toFixed(2)}, ${v.check_in_lng.toFixed(2)}` : "no GPS")}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-2">Expenses roll-up</div>
          {(expenseSummary.by_salesperson || []).length === 0 ? (
            <div className="text-sm text-[#4A5A50]">No expenses in this range.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase text-[#4A5A50] text-left">
                    <th className="py-2">Salesperson</th>
                    <th className="py-2">Submitted</th>
                    <th className="py-2">Approved</th>
                    <th className="py-2">Rejected</th>
                    <th className="py-2">Reimbursed</th>
                  </tr>
                </thead>
                <tbody>
                  {(expenseSummary.by_salesperson || []).map(r => (
                    <tr key={r.salesperson_id} className="border-t border-[#E4DFCF]">
                      <td className="py-2">{r.salesperson_name}</td>
                      <td>₹ {Math.round(r.submitted || 0).toLocaleString("en-IN")}</td>
                      <td>₹ {Math.round(r.approved || 0).toLocaleString("en-IN")}</td>
                      <td>₹ {Math.round(r.rejected || 0).toLocaleString("en-IN")}</td>
                      <td>₹ {Math.round(r.reimbursed || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Lightweight embedded map — uses OSM static tiles via an embed iframe if pins exist.
 * Keeps the bundle small (no leaflet dep) and works offline-shell-friendly.
 */
function JourneyMap({ pins }) {
  if (!pins || pins.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[#E4DFCF] p-8 text-center text-sm text-[#4A5A50] bg-[#FAF7EF]">
        <Navigation size={20} className="text-[#C9A24B] mx-auto mb-2"/>
        No pins yet. Location-tagged visits will appear here.
      </div>
    );
  }
  const bbox = pins.reduce((acc, p) => ({
    minLat: Math.min(acc.minLat, p.check_in_lat),
    maxLat: Math.max(acc.maxLat, p.check_in_lat),
    minLng: Math.min(acc.minLng, p.check_in_lng),
    maxLng: Math.max(acc.maxLng, p.check_in_lng),
  }), { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 });
  // Add small padding
  const pad = 0.02;
  const minLng = bbox.minLng - pad, minLat = bbox.minLat - pad, maxLng = bbox.maxLng + pad, maxLat = bbox.maxLat + pad;
  const marker = pins[0];
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${minLng}%2C${minLat}%2C${maxLng}%2C${maxLat}&layer=mapnik&marker=${marker.check_in_lat}%2C${marker.check_in_lng}`;
  return (
    <div className="rounded-lg overflow-hidden border border-[#E4DFCF]">
      <iframe
        title="Team journey map"
        src={src}
        className="w-full"
        style={{ height: 380, border: 0 }}
        loading="lazy"
        data-testid="journey-map-iframe"
      />
      <div className="p-2 text-[11px] text-[#4A5A50] bg-[#FAF7EF] border-t border-[#E4DFCF]">
        {pins.length} pin{pins.length === 1 ? "" : "s"} · full plotting available on <a className="underline" target="_blank" rel="noreferrer" href={`https://www.openstreetmap.org/?mlat=${marker.check_in_lat}&mlon=${marker.check_in_lng}`}>OpenStreetMap</a>
      </div>
    </div>
  );
}
